// Which composer is showing, and the browser storage behind drafts.

import { createStore } from '@/lib/store';
import type { ComposeRequest } from '@/lib/actions';
import { draftKey, pruneDrafts, readDraft, removeDraft, writeDraft, type ComposeBase, type Draft } from './logic';

/** Everything needed to put an email back in the composer exactly as it was, after Undo. */
export type ComposerSnapshot = Draft & { base: ComposeBase };

export interface ComposerState {
  status: 'closed' | 'open' | 'minimised';
  req: ComposeRequest;
  /** Changes for each new email, so the composer starts afresh. */
  seq: number;
  /** Restores this content in place of the saved draft (after Undo). */
  snapshot: ComposerSnapshot | null;
}

export const composerStore = createStore<ComposerState>({ status: 'closed', req: {}, seq: 0, snapshot: null });

export function openComposer(req: ComposeRequest = {}, snapshot: ComposerSnapshot | null = null): void {
  const s = composerStore.get();
  // The same email is already open or minimised: bring it back rather than starting again.
  if (s.status !== 'closed' && !snapshot && draftKey(s.req) === draftKey(req)) {
    composerStore.set({ status: 'open' });
    return;
  }
  composerStore.set({ status: 'open', req, seq: s.seq + 1, snapshot });
}

export function closeComposer(): void {
  composerStore.set({ status: 'closed', snapshot: null });
}

export function minimiseComposer(): void {
  if (composerStore.get().status === 'open') composerStore.set({ status: 'minimised' });
}

export function restoreComposer(): void {
  if (composerStore.get().status === 'minimised') composerStore.set({ status: 'open' });
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

let pruned = false;

export function loadDraft(userId: string, key: string): Draft | null {
  const store = storage();
  if (!store) return null;
  if (!pruned) {
    pruned = true;
    pruneDrafts(store);
  }
  return readDraft(store, userId, key);
}

export function saveDraft(userId: string, key: string, draft: Draft): void {
  const store = storage();
  if (store) writeDraft(store, userId, key, draft);
}

export function dropDraft(userId: string, key: string): void {
  const store = storage();
  if (store) removeDraft(store, userId, key);
}
